import { Alert, Box, Button, Checkbox, Stack, Typography } from '@mui/joy'
import React from 'react'
import { UploadCard } from '../Assignment/UploadCard'
import { CustomDimensions } from 'styles/theme/themes'
import { TypeExam } from 'app/constants/types/exam.types';
import { TypeSubmission } from 'app/pages/StudentAssignmentSubmissionPage/utils/types';
import { alertExamStatus } from "app/components/ExamStatus/utils/headExamStatus";
import { SUBMISSION_LIMIT } from 'app/constants/content';
import CardHelp from 'app/components/CardHelp/CardHelp'; // Import CardHelp component
import ModalConfirm from '../ModalConfirm/ModalConfirm';

export type TypeSubmissionPanelProps = {
  exam: TypeExam;
  isWithinGracePeriod: boolean;
  submissionFile?: File | FileList;
  submissions?: { rawData?: { submissionCount?: number } };
  handleRemoveFile: () => void;
  MAX_FILE_SIZE: number;
  handleSelectFile: (file?: File | FileList) => void;
  verificationChecks: {
    selfDone: boolean;
    timeLimit: boolean;
    confirmTruth: boolean;
  };
  handleVerificationChange: (key: string, value: boolean) => void;
  handleSubmit: () => void;
  isSubmitting: boolean;
  isPolling: boolean;
  hasSubmission?: TypeSubmission;
  currentUserSubmission?: TypeSubmission,
  fetchSubmissionStatus: () => void;
  isModalOpen: boolean;
  handleCloseModal: () => void;
  handleConfirmSubmit: () => void;
};

const SubmissionPanel = ({
  isModalOpen,
  handleCloseModal,
  handleConfirmSubmit,
  exam,
  isWithinGracePeriod,
  submissionFile,
  submissions,
  handleRemoveFile,
  MAX_FILE_SIZE,
  handleSelectFile,
  verificationChecks,
  handleVerificationChange,
  handleSubmit,
  isSubmitting,
  isPolling,
  hasSubmission,
  currentUserSubmission,
  fetchSubmissionStatus,
}: TypeSubmissionPanelProps) => {

  // const isSubmissionAllowed = false
  const isSubmissionAllowed = SUBMISSION_LIMIT > 0 ? (submissions && ((submissions?.rawData?.submissionCount ?? 0) < SUBMISSION_LIMIT)) : true

  return (
    <Box
      sx={{
        mb: 3,
        gap: 2,
        display:
          exam.status == "ongoing" || exam.status == "missing" || (exam.status == "ended" && isWithinGracePeriod)
            ? "grid"
            : "none",
      }}
    >

      {!isSubmissionAllowed && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
            borderRadius: CustomDimensions.cornerRadius,
            p: 3,
            mb: 2,
          }}
        >
          <Typography level="h4" sx={{ mb: 1 }}>
            Submission Limit Reached
          </Typography>
          <Typography level="body-sm" textAlign="center" sx={{}}>
            You’ve reached the maximum submission limit of {SUBMISSION_LIMIT}. Please review your submissions carefully.
          </Typography>
          <Typography level="body-sm" textAlign="center" sx={{ mb: 2 }}>
            Need help? Our assistant is available to guide you right away.
          </Typography>
          <CardHelp slot={{ button: <Typography level="body-sm" sx={{ cursor: 'pointer', ml: 0.7 }}>Get Assistance</Typography> }} />
        </Box>
      )}

      {isSubmissionAllowed && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography level="h4">Submission</Typography>
          </Stack>
          <UploadCard
            slot={
              exam.status == "missing"
                ? {
                  alert: (
                    <Alert
                      variant={"soft"}
                      color="danger"
                      sx={{ maxWidth: "800px" }}
                    >
                      {alertExamStatus.missing}
                    </Alert>
                  ),
                }
                : undefined
            }
            title="Upload Submission"
            description="Drag and drop your submission here, or click to select file"
            file={submissionFile}
            disabled={!isSubmissionAllowed}
            onRemove={handleRemoveFile}
            MAX_FILE_SIZE={MAX_FILE_SIZE}
            setFile={handleSelectFile}
          />
          {
            (exam.status == "ongoing" || exam.status == "missing" || (exam.status == "ended" && isWithinGracePeriod)) && (
              <>
                <Stack spacing={2} sx={{ my: 2 }}>
                  <Stack spacing={1}>
                    <Checkbox
                      checked={verificationChecks.selfDone}
                      onChange={(e) =>
                        handleVerificationChange(
                          "selfDone",
                          e.target.checked
                        )
                      }
                      slotProps={{
                        label: { sx: { typography: "body-md" } },
                      }}
                      label="I confirm that this paper is done by myself."
                    />
                    <Checkbox
                      checked={verificationChecks.timeLimit}
                      onChange={(e) =>
                        handleVerificationChange(
                          "timeLimit",
                          e.target.checked
                        )
                      }
                      slotProps={{
                        label: { sx: { typography: "body-md" } },
                      }}
                      label="I confirm that this paper is done within the time limit."
                    />
                    <Checkbox
                      checked={verificationChecks.confirmTruth}
                      onChange={(e) =>
                        handleVerificationChange(
                          "confirmTruth",
                          e.target.checked
                        )
                      }
                      slotProps={{
                        label: { sx: { typography: "body-md" } },
                      }}
                      label="I affirm the truthfulness and accuracy of my responses to the statements above."
                    />
                  </Stack>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }} // Stack vertically on small screens
                  spacing={2}
                  sx={{ width: "100%" }}
                >
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      isPolling ||
                      !submissionFile ||
                      !verificationChecks.selfDone ||
                      !verificationChecks.timeLimit ||
                      !verificationChecks.confirmTruth
                    }
                    sx={{
                      borderRadius: CustomDimensions.cornerRadius,
                      fontSize: { xs: "1rem", sm: "1.2rem" }, // Responsive font size
                      py: { xs: 1, sm: 0.5 }, // More padding on small screens
                      flex: 1,
                      width: "100%", // Full width on all screens
                      mb: { xs: 2, sm: 0 }, // Add margin bottom on small screens
                    }}
                  >
                    {hasSubmission ? "Re-submit" : "Submit"}
                  </Button>

                  <ModalConfirm
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmSubmit}
                    title="Submit Paper"
                    description={`Are you sure you want to submit your paper? This action cannot be undone.`}
                    caption={`Please note that you have a submission limit of ${SUBMISSION_LIMIT} attempts.`}
                  />

                  {!isPolling &&
                    !currentUserSubmission?.submissionArtifact && (
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={fetchSubmissionStatus}
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: CustomDimensions.cornerRadius,
                          fontSize: { xs: "0.9rem", sm: "1rem" }, // Responsive font size
                          py: { xs: 1, sm: 0.5 }, // More padding on small screens
                          width: { xs: "100%", sm: "auto" }, // Full width on small screens
                        }}
                      >
                        Check Status
                      </Button>
                    )}
                </Stack>
              </>
            )
          }
        </>
      )}

    </Box >
  )
}

export default SubmissionPanel